import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import {
  addStudent, addLead, updateLead, getUnassignedLeads, getSalespeople, getMentors,
  markLeadClassOver, markLeadAlreadyAssigned,
} from '../../lib/api';
import type { UnassignedLead, MentorInfo, SalesPersonInfo } from '../../lib/api';

// ── helpers ──────────────────────────────────────────────────────────────────

function initials(name: string) {
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

// `sessionCategory` ('online' | 'offline') is the accurate field for Pay
// After Placement group-session leads. Older leads don't have it, so fall
// back to the legacy sessionType heuristic ('one-to-one' vs 'group').
function sessionLabel(sessionType: string | undefined, sessionCategory?: string) {
  if (sessionCategory === 'online')  return 'Online';
  if (sessionCategory === 'offline') return 'Offline';
  return (sessionType || '').toLowerCase().includes('one') ? 'Online' : 'Offline';
}

function sessionStyle(sessionType: string | undefined, sessionCategory?: string): React.CSSProperties {
  const isOnline = sessionCategory ? sessionCategory === 'online' : (sessionType || '').toLowerCase().includes('one');
  return isOnline
    ? { background: '#ede9fe', color: '#5b21b6' }
    : { background: '#dcfce7', color: '#15803d' };
}

const SLOT_LABELS: Record<string, string> = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' };

// Pay-After-Placement group-session leads carry extra detail (college, degree,
// year, online/offline, preferred time slot) that plain leads don't have.
// College/degree/year — secondary academic info, fine to truncate on a
// long name since it's not needed to actually schedule the student.
function leadExtraDetails(l: UnassignedLead): string {
  const parts: string[] = [];
  if (l.collegeName) parts.push(l.collegeName);
  if (l.degree || l.yearOfPassout) parts.push([l.degree, l.yearOfPassout].filter(Boolean).join(' '));
  return parts.join(' · ');
}

// The picked time slot — needed to actually schedule the student, so it
// gets its own always-visible line rather than risking truncation behind
// a long college name in leadExtraDetails. (Online/Offline already has its
// own dedicated Session column badge, so it isn't repeated here.)
function leadSlotLabel(l: UnassignedLead): string | null {
  if (!l.slotPreference) return null;
  return '🕐 ' + (SLOT_LABELS[l.slotPreference] || l.slotPreference);
}

// Only Pay-After-Placement group-session leads have sessionCategory set —
// for those, show running total-paid / remaining-balance (before-placement
// target only; after-placement isn't collected through this flow). The target
// is the fee they signed up at, else the standard fee for their mode.
function groupBalanceSummary(l: UnassignedLead): string | null {
  if (!l.sessionCategory) return null;
  const beforeTarget = l.courseFee > 0 ? l.courseFee : l.sessionCategory === 'offline' ? 20000 : 15000;
  const totalPaid = l.totalPaid || 0;
  const remaining = Math.max(0, beforeTarget - totalPaid);
  return remaining > 0
    ? `Paid ₹${totalPaid.toLocaleString('en-IN')} · Bal ₹${remaining.toLocaleString('en-IN')}`
    : `Paid ₹${totalPaid.toLocaleString('en-IN')} · Fully Paid ✓`;
}

// ── animation styles ─────────────────────────────────────────────────────────

const ANIM_CSS = `
@keyframes ulFadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
@keyframes ulPulse  { 0%,100%{ opacity:1; } 50%{ opacity:.55; } }
.ul-row { animation: ulFadeIn .32s ease both; }
.ul-pulse { animation: ulPulse 2s ease infinite; }
`;

// ── ADD LEAD MODAL ────────────────────────────────────────────────────────────

const AddLeadModal: React.FC<{
  adminPassword: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ adminPassword, onClose, onDone }) => {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', course: '', sessionType: 'group',
    regPaid: '1000', courseFee: '', salesPerson: '',
    enrollDate: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await addLead(adminPassword, {
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(),
        course: form.course.trim(), sessionType: form.sessionType,
        regPaid: Number(form.regPaid) || 0, courseFee: Number(form.courseFee) || 0,
        salesPerson: form.salesPerson.trim(), enrollDate: form.enrollDate,
      });
      toast.success(`Lead added: ${form.name}`);
      onDone();
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
          <div>
            <p className="text-white font-bold text-base">Add New Lead</p>
            <p className="text-white/60 text-xs mt-0.5">Register a paid enquiry</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Full Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} required
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 transition-colors"
                  placeholder="Student full name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Phone</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 transition-colors"
                  placeholder="9xxxxxxxxx" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 transition-colors"
                  placeholder="email@example.com" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Course</label>
                <input value={form.course} onChange={e => set('course', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 transition-colors"
                  placeholder="Python Full-Stack, Data Analytics…" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Session Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['group', 'one-to-one'] as const).map(t => (
                    <button key={t} type="button" onClick={() => set('sessionType', t)}
                      className="py-2.5 rounded-xl text-xs font-bold border-2 transition-all"
                      style={form.sessionType === t
                        ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                        : { background: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb' }}>
                      {t === 'group' ? '🏢 Offline — ₹20,000' : '💻 Online — ₹15,000'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reg Paid (₹)</label>
                <input type="number" value={form.regPaid} onChange={e => set('regPaid', e.target.value)} min="0"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Course Fee (₹)</label>
                <input type="number" value={form.courseFee} onChange={e => set('courseFee', e.target.value)} min="0"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 transition-colors"
                  placeholder="15000" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Enroll Date</label>
                <input type="date" value={form.enrollDate} onChange={e => set('enrollDate', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Sales Person</label>
                <input value={form.salesPerson} onChange={e => set('salesPerson', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 transition-colors"
                  placeholder="Name" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60 transition-all"
                style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
                {saving ? 'Adding…' : 'Add Lead'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ── EDIT LEAD MODAL ───────────────────────────────────────────────────────────

const EditLeadModal: React.FC<{
  lead: UnassignedLead;
  adminPassword: string;
  onClose: () => void;
  onDone: (updated: Partial<UnassignedLead>) => void;
}> = ({ lead, adminPassword, onClose, onDone }) => {
  const [sessionType, setSessionType] = useState(
    (lead.sessionType || '').toLowerCase().includes('one') ? 'one-to-one' : 'group'
  );
  const [course, setCourse] = useState(lead.course || '');
  const [salesPerson, setSalesPerson] = useState(lead.salesPerson || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateLead(adminPassword, lead._id, { sessionType, course, salesPerson });
      toast.success('Lead updated');
      onDone({ sessionType, course, salesPerson });
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm z-10 overflow-hidden">
        <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg,#1e3a5f,#2b5f8a)' }}>
          <p className="text-white font-bold text-base">Edit Lead</p>
          <p className="text-white/60 text-xs mt-0.5">{lead.name}</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Session Type</label>
            <div className="grid grid-cols-2 gap-2">
              {(['group', 'one-to-one'] as const).map(t => (
                <button key={t} type="button" onClick={() => setSessionType(t)}
                  className="py-2.5 rounded-xl text-xs font-bold border-2 transition-all"
                  style={sessionType === t
                    ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                    : { background: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb' }}>
                  {t === 'group' ? '🏢 Offline' : '💻 Online'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Course</label>
            <input value={course} onChange={e => setCourse(e.target.value)}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400"
              placeholder="Course name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Sales Person</label>
            <input value={salesPerson} onChange={e => setSalesPerson(e.target.value)}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400"
              placeholder="Name" />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#1e3a5f,#2b5f8a)' }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── ACTION MODAL ──────────────────────────────────────────────────────────────

const ActionModal: React.FC<{
  lead: UnassignedLead;
  onClose: () => void;
  onClassOver: () => void;
  onAlreadyAssigned: () => void;
  onAssign: () => void;
  onEdit: () => void;
}> = ({ lead, onClose, onClassOver, onAlreadyAssigned, onAssign, onEdit }) => (
  <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm z-10 overflow-hidden">
      <div className="px-5 py-4" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
        <p className="text-white font-bold">{lead.name}</p>
        <p className="text-white/60 text-xs mt-0.5">{lead.course || 'No course'} · {sessionLabel(lead.sessionType, lead.sessionCategory)}</p>
        {leadExtraDetails(lead) && (
          <p className="text-white/50 text-xs mt-1">{leadExtraDetails(lead)}</p>
        )}
        {leadSlotLabel(lead) && (
          <p className="text-amber-300 text-xs mt-1 font-semibold">{leadSlotLabel(lead)}</p>
        )}
        {groupBalanceSummary(lead) && (
          <p className="text-amber-300 text-xs mt-1 font-semibold">{groupBalanceSummary(lead)}</p>
        )}
      </div>
      <div className="p-4 space-y-2">
        <button onClick={onAssign}
          className="w-full py-3 rounded-xl text-sm font-bold text-white text-left px-4 flex items-center gap-3 transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
          <span className="text-xl">📚</span>
          <div>
            <div>Assign to Class</div>
            <div className="text-xs font-normal opacity-70">Pick mentor, confirm session type & send email</div>
          </div>
        </button>
        <button onClick={onEdit}
          className="w-full py-3 rounded-xl text-sm font-bold text-left px-4 flex items-center gap-3 border-2 transition-all hover:bg-blue-50"
          style={{ borderColor: '#3b82f6', color: '#1d4ed8' }}>
          <span className="text-xl">✏️</span>
          <div>
            <div>Edit Lead Details</div>
            <div className="text-xs font-normal opacity-70">Fix session type, course or salesperson</div>
          </div>
        </button>
        <button onClick={onClassOver}
          className="w-full py-3 rounded-xl text-sm font-bold text-left px-4 flex items-center gap-3 border-2 transition-all hover:bg-emerald-50"
          style={{ borderColor: '#059669', color: '#065f46' }}>
          <span className="text-xl">✅</span>
          <div>
            <div>Class Over</div>
            <div className="text-xs font-normal opacity-70">Training completed — remove from list</div>
          </div>
        </button>
        <button onClick={onAlreadyAssigned}
          className="w-full py-3 rounded-xl text-sm font-bold text-left px-4 flex items-center gap-3 border-2 transition-all hover:bg-amber-50"
          style={{ borderColor: '#d97706', color: '#92400e' }}>
          <span className="text-xl">🔍</span>
          <div>
            <div>Already Assigned</div>
            <div className="text-xs font-normal opacity-70">Duplicate — exists under a different name</div>
          </div>
        </button>
        <button onClick={onClose}
          className="w-full py-2.5 rounded-xl text-sm font-medium text-gray-500 border-2 border-gray-200 hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// ── ASSIGN CLASS MODAL ────────────────────────────────────────────────────────

const AssignModal: React.FC<{
  lead: UnassignedLead;
  mentors: MentorInfo[];
  salespeople: SalesPersonInfo[];
  adminPassword: string;
  onClose: () => void;
  onDone: (id: string) => void;
}> = ({ lead, mentors, salespeople, adminPassword, onClose, onDone }) => {
  const [mentorId, setMentorId] = useState('');
  const [course, setCourse] = useState(lead.course || '');
  const [sessionType, setSessionType] = useState<'one-to-one' | 'group'>(
    (lead.sessionType || '').toLowerCase().includes('one') ? 'one-to-one' : 'group'
  );
  const [salesPerson, setSalesPerson] = useState(lead.salesPerson || '');
  const [regPaid, setRegPaid] = useState(String(lead.regPaid || 1000));
  const [notes, setNotes] = useState('');
  const [demoTime, setDemoTime] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mentorId) { toast.error('Please select a mentor'); return; }
    if (!course.trim()) { toast.error('Please enter a course'); return; }
    setSaving(true);
    try {
      await addStudent(adminPassword, lead.name, course.trim(), mentorId, notes,
        lead.email, sendEmail, sessionType, salesPerson, Number(regPaid) || 1000, 0, lead.phone || '', demoTime || '');
      // Mark payment record closed so it never re-appears in unassigned leads
      await markLeadAlreadyAssigned(adminPassword, lead._id).catch(() => {});
      toast.success(`${lead.name} assigned!${sendEmail ? ' Welcome email sent.' : ''}`);
      onDone(lead._id);
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
          <p className="text-white font-bold text-base">Assign to Class</p>
          <p className="text-white/60 text-xs mt-0.5">{lead.name} · {lead.email || lead.phone || '—'}</p>
        </div>
        <div className="overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Course *
                {!lead.course && <span className="ml-1 text-red-500 normal-case font-normal">(not set — please enter)</span>}
              </label>
              <input value={course} onChange={e => setCourse(e.target.value)} required
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400"
                placeholder="Full Stack, Data Analytics…" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Session Type</label>
              <div className="grid grid-cols-2 gap-2">
                {(['group', 'one-to-one'] as const).map(t => (
                  <button key={t} type="button" onClick={() => setSessionType(t)}
                    className="py-2.5 rounded-xl text-xs font-bold border-2 transition-all"
                    style={sessionType === t
                      ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                      : { background: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb' }}>
                    {t === 'group' ? '🏢 Offline — ₹20,000' : '💻 Online — ₹15,000'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Assign Mentor *</label>
              <select value={mentorId} onChange={e => setMentorId(e.target.value)} required
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 bg-white">
                <option value="">— Select mentor —</option>
                {mentors.map(m => <option key={m.mentorId} value={m.mentorId}>{m.mentorName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Salesperson</label>
              <select value={salesPerson} onChange={e => setSalesPerson(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 bg-white">
                <option value="">— None —</option>
                {salespeople.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Registration Paid (₹)</label>
              <input type="number" value={regPaid} onChange={e => setRegPaid(e.target.value)} min="0"
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 resize-none"
                placeholder="Any special instructions…" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                📅 Demo / First Class Time
                <span className="ml-1 text-gray-400 normal-case font-normal">(optional — included in welcome email)</span>
              </label>
              <input
                type="datetime-local"
                value={demoTime}
                onChange={e => setDemoTime(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400"
              />
              {demoTime && (
                <p className="mt-1.5 text-xs text-purple-600 font-medium">
                  ✓ {new Date(demoTime).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })} will appear in the welcome email
                </p>
              )}
            </div>
            {lead.email && (
              <label className="flex items-center gap-2.5 cursor-pointer select-none p-3 rounded-xl bg-purple-50 border border-purple-100">
                <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)}
                  className="w-4 h-4 rounded accent-purple-700" />
                <span className="text-xs text-gray-700">Send welcome email to <span className="font-semibold text-purple-700">{lead.email}</span></span>
              </label>
            )}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
                {saving ? 'Assigning…' : 'Assign & Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────

const UnassignedLeads: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const [leads, setLeads] = useState<UnassignedLead[]>([]);
  const [mentors, setMentors] = useState<MentorInfo[]>([]);
  const [salespeople, setSalespeople] = useState<SalesPersonInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sessionFilter, setSessionFilter] = useState<'all' | 'online' | 'offline'>('all');
  const [showAddLead, setShowAddLead] = useState(false);
  const [actionLead, setActionLead] = useState<UnassignedLead | null>(null);
  const [editLead, setEditLead] = useState<UnassignedLead | null>(null);
  const [assignLead, setAssignLead] = useState<UnassignedLead | null>(null);

  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = ANIM_CSS;
    document.head.appendChild(el);
    styleRef.current = el;
    return () => { el.remove(); };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, mentorRes, spRes] = await Promise.all([
        getUnassignedLeads(adminPassword),
        getMentors(),
        getSalespeople(adminPassword),
      ]);
      setLeads(leadsRes.leads ?? []);
      setMentors((mentorRes.mentors ?? []).filter(m => m.status !== 'Inactive'));
      setSalespeople(spRes.salespeople ?? []);
    } catch (err: any) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const removeById = (id: string) => setLeads(prev => prev.filter(l => l._id !== id));
  const patchById  = (id: string, patch: Partial<UnassignedLead>) =>
    setLeads(prev => prev.map(l => l._id === id ? { ...l, ...patch } : l));

  const handleAlreadyAssigned = async (lead: UnassignedLead) => {
    try {
      await markLeadAlreadyAssigned(adminPassword, lead._id);
      toast.success(`${lead.name} marked as Already Assigned`);
      setActionLead(null);
      removeById(lead._id);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleClassOver = async (lead: UnassignedLead) => {
    try {
      await markLeadClassOver(adminPassword, lead._id);
      toast.success(`${lead.name} marked as Class Over`);
      setActionLead(null);
      removeById(lead._id);
    } catch (err: any) { toast.error(err.message); }
  };

  const onlineCount  = leads.filter(l => (l.sessionType || '').toLowerCase().includes('one')).length;
  const offlineCount = leads.length - onlineCount;
  const totalReg     = leads.reduce((s, l) => s + (l.regPaid || 0), 0);

  const visible = leads.filter(l => {
    if (sessionFilter === 'online'  && !(l.sessionType || '').toLowerCase().includes('one')) return false;
    if (sessionFilter === 'offline' &&  (l.sessionType || '').toLowerCase().includes('one')) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return l.name.toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q) ||
      (l.phone || '').includes(q) ||
      (l.course || '').toLowerCase().includes(q);
  });

  const STAT_TABS: { key: 'all' | 'online' | 'offline'; label: string; count: number; icon: string; color: string; bg: string }[] = [
    { key: 'all',     label: 'All Leads',   count: leads.length,  icon: '📋', color: '#2b2240', bg: '#f3f0ff' },
    { key: 'online',  label: 'Online',      count: onlineCount,   icon: '💻', color: '#5b21b6', bg: '#ede9fe' },
    { key: 'offline', label: 'Offline',     count: offlineCount,  icon: '🏢', color: '#15803d', bg: '#dcfce7' },
  ];

  return (
    <TrackerLayout>
      <div className="space-y-5">

        {/* ── header ── */}
        <div className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">🎯</span>
                <h1 className="text-xl font-bold text-white">Unassigned Leads</h1>
                {leads.length > 0 && (
                  <span className="ul-pulse px-2.5 py-0.5 rounded-full text-xs font-bold text-white"
                    style={{ background: '#FF6535' }}>
                    {leads.length} Pending
                  </span>
                )}
              </div>
              <p className="text-white/50 text-xs">Paid registration — not yet assigned to a class</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowAddLead(true)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>
                + Add Lead
              </button>
              <button onClick={load}
                className="px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
                ↻ Refresh
              </button>
            </div>
          </div>

          {/* stat cards row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border-t border-white/10">
            {STAT_TABS.map(s => (
              <button key={s.key} onClick={() => setSessionFilter(s.key)}
                className="flex items-center gap-3 px-5 py-4 transition-all hover:bg-white/10"
                style={sessionFilter === s.key ? { background: 'rgba(255,255,255,0.15)' } : {}}>
                <span className="text-xl">{s.icon}</span>
                <div className="text-left">
                  <div className="text-2xl font-bold text-white leading-none">{s.count}</div>
                  <div className="text-white/50 text-xs mt-0.5">{s.label}</div>
                </div>
                {sessionFilter === s.key && (
                  <div className="ml-auto w-1.5 h-8 rounded-full bg-yellow-400" />
                )}
              </button>
            ))}
            <div className="flex items-center gap-3 px-5 py-4">
              <span className="text-xl">💰</span>
              <div className="text-left">
                <div className="text-xl font-bold text-white leading-none">
                  ₹{totalReg.toLocaleString('en-IN')}
                </div>
                <div className="text-white/50 text-xs mt-0.5">Reg Collected</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── search + filter ── */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input type="text" placeholder="Search name, email, phone, course…"
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400 transition-colors bg-white" />
          </div>
          <div className="flex gap-1 p-1 rounded-xl bg-gray-100">
            {STAT_TABS.map(s => (
              <button key={s.key} onClick={() => setSessionFilter(s.key)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={sessionFilter === s.key
                  ? { background: '#2b2240', color: '#fff' }
                  : { color: '#6b7280' }}>
                {s.icon} {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── content ── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 rounded-full border-3 border-purple-200 border-t-purple-600 animate-spin"
              style={{ borderWidth: 3 }} />
            <p className="text-sm text-gray-400">Loading leads…</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-14 text-center">
            <div className="text-5xl mb-3">{search || sessionFilter !== 'all' ? '🔍' : '🎉'}</div>
            <p className="text-gray-700 font-bold text-base">
              {search || sessionFilter !== 'all' ? 'No leads match your filter' : 'No unassigned leads'}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {search || sessionFilter !== 'all'
                ? 'Try clearing the search or switching tabs.'
                : 'Everyone who paid has been assigned or resolved.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg,#2b2240,#3d2f5f)' }}>
                    {['#', 'Student', 'Course', 'Session', 'Phone', 'Email', 'Reg Paid', 'Date', ''].map(h => (
                      <th key={h} className="px-3 py-3.5 text-left text-xs font-semibold text-white/80 whitespace-nowrap tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((l, idx) => (
                    <tr key={l._id}
                      className="ul-row border-b border-gray-100 hover:bg-purple-50/30 transition-colors"
                      style={{ animationDelay: `${idx * 0.04}s` }}>
                      <td className="px-3 py-3.5 text-xs text-gray-400 font-medium">{idx + 1}</td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
                            {initials(l.name)}
                          </div>
                          <span className="font-semibold text-gray-800 whitespace-nowrap">{l.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3.5 max-w-[170px]">
                        <span className="text-xs text-purple-700 font-medium truncate block max-w-[150px]"
                          title={l.course || ''}>
                          {l.course || <span className="text-gray-300">—</span>}
                        </span>
                        {leadExtraDetails(l) && (
                          <span className="text-[10px] text-gray-400 truncate block max-w-[150px]" title={leadExtraDetails(l)}>
                            {leadExtraDetails(l)}
                          </span>
                        )}
                        {leadSlotLabel(l) && (
                          <span className="text-[10px] text-amber-600 font-semibold block whitespace-nowrap">{leadSlotLabel(l)}</span>
                        )}
                      </td>
                      <td className="px-3 py-3.5">
                        <span className="text-xs px-2.5 py-1 rounded-lg font-semibold"
                          style={sessionStyle(l.sessionType, l.sessionCategory)}>
                          {sessionLabel(l.sessionType, l.sessionCategory)}
                        </span>
                      </td>
                      <td className="px-3 py-3.5 text-xs text-gray-600 whitespace-nowrap font-medium">
                        {l.phone
                          ? <a href={`tel:${l.phone}`} className="hover:text-purple-700 transition-colors">{l.phone}</a>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-3.5 max-w-[160px]">
                        <span className="text-xs text-gray-500 truncate block max-w-[145px]" title={l.email || ''}>
                          {l.email || <span className="text-gray-300">—</span>}
                        </span>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className="text-xs font-bold text-gray-700">₹{(l.regPaid || 0).toLocaleString('en-IN')}</span>
                        {groupBalanceSummary(l) && (
                          <span className="text-[10px] text-gray-400 block whitespace-nowrap">{groupBalanceSummary(l)}</span>
                        )}
                      </td>
                      <td className="px-3 py-3.5 text-xs text-gray-400 whitespace-nowrap">{l.enrollDate || '—'}</td>
                      <td className="px-3 py-3.5">
                        <button onClick={() => setActionLead(l)}
                          className="px-3.5 py-1.5 rounded-lg text-xs font-bold border-2 whitespace-nowrap transition-all hover:shadow-sm active:scale-95"
                          style={{ borderColor: '#F0B429', color: '#2b2240', background: '#fefce8' }}>
                          Action ▾
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-400">Showing <b className="text-gray-600">{visible.length}</b> of <b className="text-gray-600">{leads.length}</b> leads</span>
              {sessionFilter !== 'all' && (
                <button onClick={() => setSessionFilter('all')}
                  className="text-xs text-purple-600 hover:text-purple-800 font-medium">Clear filter</button>
              )}
            </div>
          </div>
        )}
      </div>

      {showAddLead && (
        <AddLeadModal adminPassword={adminPassword}
          onClose={() => setShowAddLead(false)}
          onDone={() => { setShowAddLead(false); load(); }} />
      )}

      {actionLead && !assignLead && !editLead && (
        <ActionModal lead={actionLead}
          onClose={() => setActionLead(null)}
          onClassOver={() => handleClassOver(actionLead)}
          onAlreadyAssigned={() => handleAlreadyAssigned(actionLead)}
          onAssign={() => { setAssignLead(actionLead); setActionLead(null); }}
          onEdit={() => { setEditLead(actionLead); setActionLead(null); }} />
      )}

      {editLead && (
        <EditLeadModal lead={editLead} adminPassword={adminPassword}
          onClose={() => setEditLead(null)}
          onDone={updated => { patchById(editLead._id, updated); setEditLead(null); }} />
      )}

      {assignLead && (
        <AssignModal lead={assignLead} mentors={mentors} salespeople={salespeople}
          adminPassword={adminPassword}
          onClose={() => setAssignLead(null)}
          onDone={id => { setAssignLead(null); removeById(id); }} />
      )}
    </TrackerLayout>
  );
};

export default UnassignedLeads;
